import type {VariableKind} from "./types";

/**
 * The format vocabulary, per kind.
 *
 * These strings are stored on the chip and read again by whoever renders the value to a reader —
 * the guest app resolves the same `format` attribute against its own copy of this file. A key
 * renamed here is a key nobody downstream recognises, so they are added to, never edited.
 */
export const FORMATS: Partial<Record<VariableKind, readonly string[]>> = {
    date: ["numeric", "short", "long", "weekday"],
    time: ["24h", "12h"],
    currency: ["symbol", "code", "plain"],
    list: ["and", "comma"],
    boolean: ["yesNo", "symbol"],
    duration: ["hoursMinutes", "minutes"],
    greeting: ["formal", "friendly"],
};

/** Stand-in values used to render format previews when no context value exists. */
export const EXAMPLE_VALUES: Partial<Record<VariableKind, unknown>> = {
    date: "2026-09-01",
    time: "14:30",
    currency: 129,
    list: ["Anna", "Ben"],
    boolean: true,
    duration: 90,
    greeting: {
        formal: {de: "Sehr geehrte Frau Bauer", en: "Dear Ms Bauer", it: "Gentile Signora Bauer"},
        friendly: {de: "Liebe Anna", en: "Dear Anna", it: "Cara Anna"},
    },
};

export const LOCALES: Record<string, string> = {de: "de-AT", en: "en-GB", it: "it-IT"};

export const DATE_FORMAT_PARTS: Record<string, Intl.DateTimeFormatOptions> = {
    numeric: {day: "2-digit", month: "2-digit", year: "numeric"},
    short: {day: "numeric", month: "short", year: "numeric"},
    long: {day: "numeric", month: "long", year: "numeric"},
    weekday: {weekday: "long", day: "numeric", month: "long", year: "numeric"},
};

export const formatDate = (value: unknown, locale: string, format: string) => {
    const date = new Date(String(value));
    if (Number.isNaN(date.getTime())) return "";

    return new Intl.DateTimeFormat(locale, {
        ...(DATE_FORMAT_PARTS[format] ?? DATE_FORMAT_PARTS.numeric),
        timeZone: "UTC",
    }).format(date);
};

export const formatTime = (value: unknown, locale: string, format: string) => {
    const [hours, minutes] = String(value).split(":").map(Number);
    if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return "";

    return new Intl.DateTimeFormat(locale, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: format === "12h",
        timeZone: "UTC",
    }).format(new Date(Date.UTC(1970, 0, 1, hours, minutes)));
};

export const formatNumber = (value: unknown, locale: string) =>
    typeof value === "number" ? new Intl.NumberFormat(locale).format(value) : "";

export const formatCurrency = (
    value: unknown,
    locale: string,
    format: string,
    currency: string,
) => {
    if (typeof value !== "number") return "";

    // 'plain' formats as currency and strips the symbol to preserve locale-specific grouping.
    if (format === "plain") {
        return new Intl.NumberFormat(locale, {style: "currency", currency})
            .formatToParts(value)
            .filter(part => part.type !== "currency")
            .map(part => part.value)
            .join("")
            .trim();
    }

    return new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        currencyDisplay: format === "code" ? "code" : "symbol",
    }).format(value);
};

export const formatList = (value: unknown, locale: string, format: string) => {
    if (!Array.isArray(value)) return "";
    const items = value.map(String);

    return format === "comma"
        ? items.join(", ")
        : new Intl.ListFormat(locale, {style: "long", type: "conjunction"}).format(items);
};

export const YES_NO: Record<string, readonly [string, string]> = {
    de: ["Ja", "Nein"],
    en: ["Yes", "No"],
    it: ["Sì", "No"],
};

export const formatBoolean = (value: unknown, language: string, format: string) => {
    if (typeof value !== "boolean") return "";
    if (format === "symbol") return value ? "✓" : "—";

    const [yes, no] = YES_NO[language] ?? YES_NO.en;
    return value ? yes : no;
};

export const formatDuration = (value: unknown, format: string) => {
    if (typeof value !== "number") return "";
    if (format === "minutes" || value < 60) return `${value} min`;

    const hours = Math.floor(value / 60);
    const minutes = value % 60;
    return minutes ? `${hours} h ${minutes} min` : `${hours} h`;
};

/**
 * A greeting arrives composed, per format and then per language: only whoever holds the guest
 * profile knows the gender, the title and which names exist. This only picks one — the format asked
 * for (anything else is the formal default), in the language asked for, else in English. A plain
 * string is a greeting picked already.
 */
export const formatGreeting = (value: unknown, language: string, format: string) => {
    if (typeof value === "string") return value;

    const greetings = (value ?? {}) as Record<string, Record<string, string> | undefined>;
    const forms = greetings[format === "friendly" ? "friendly" : "formal"];

    return forms?.[language] ?? forms?.en ?? "";
};

export const localizedValue = (value: unknown, language: string) => {
    // A catalogue that localizes before the formatter sees the value hands over a plain string;
    // indexing one by a language code would answer with its first character.
    if (typeof value === "string") return value;

    const translations = (value ?? {}) as Record<string, string>;

    return translations[language] ?? Object.values(translations)[0] ?? "";
};

export const formatValue = (
    kind: VariableKind,
    value: unknown,
    {language, format, currency}: {language: string; format: string; currency: string},
) => {
    const locale = LOCALES[language] ?? language;

    switch (kind) {
        case "localizedText":
            return localizedValue(value, language);
        case "date":
            return formatDate(value, locale, format);
        case "time":
            return formatTime(value, locale, format);
        case "number":
            return formatNumber(value, locale);
        case "currency":
            return formatCurrency(value, locale, format, currency);
        case "list":
            return formatList(value, locale, format);
        case "boolean":
            return formatBoolean(value, language, format);
        case "duration":
            return formatDuration(value, format);
        case "greeting":
            return formatGreeting(value, language, format);
        default:
            return value == null ? "" : String(value);
    }
};
